import React, { useEffect, useState } from 'react';
import { Grid, CircularProgress, Chip, makeStyles, Typography, Box, Tooltip, Card, CardContent } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import CloudIcon from '@material-ui/icons/Cloud';

const useStyles = makeStyles(theme => ({
  instructionBox: {
    backgroundColor: theme.palette.type === 'dark' ? '#1a2332' : '#e8f4fd',
    border: `1px solid ${theme.palette.type === 'dark' ? '#2d4a6e' : '#90caf9'}`,
    borderLeft: `4px solid ${theme.palette.type === 'dark' ? '#4a90d9' : '#1976d2'}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  instructionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.type === 'dark' ? '#90caf9' : '#1565c0',
  },
  instructionStep: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  stepSeparator: {
    borderTop: `1px solid ${theme.palette.type === 'dark' ? '#2d4a6e' : '#90caf9'}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  instructionFooter: {
    marginTop: theme.spacing(2),
  },
  commandHint: {
    marginTop: -4,
    marginBottom: 4,
    display: 'block',
  },
  instructionIntro: {
    marginBottom: 0,
  },
  codeBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.type === 'dark' ? '#0d1117' : '#f6f8fa',
    border: `1px solid ${theme.palette.type === 'dark' ? '#30363d' : '#d0d7de'}`,
    borderRadius: 6,
    padding: '8px 12px',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    wordBreak: 'break-all',
  },
  serverCard: {
    height: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  serverCardTop: {
    marginBottom: theme.spacing(1.5),
  },
  serverName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    lineHeight: 1.3,
  },
  serverDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
    lineHeight: 1.4,
  },
  serverCardActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  comingSoonChip: {
    fontSize: '0.7rem',
    height: 28,
    backgroundColor: theme.palette.type === 'dark' ? '#663c00' : '#fff3cd',
    color: theme.palette.type === 'dark' ? '#ffa500' : '#ff8c00',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: theme.palette.type === 'dark' ? '#ffa500' : '#ff8c00',
    },
  },
  availableChip: {
    fontSize: '0.7rem',
    height: 28,
    backgroundColor: theme.palette.type === 'dark' ? '#1a4d1a' : '#d4edda',
    color: theme.palette.type === 'dark' ? '#4caf50' : '#2e7d32',
    '& .MuiChip-icon': {
      color: theme.palette.type === 'dark' ? '#4caf50' : '#2e7d32',
    },
  },
  installButton: {
    display: 'inline-block',
    lineHeight: 0,
    '& img': {
      transition: 'opacity 0.15s ease',
    },
    '&:hover img': {
      opacity: 0.85,
    },
  },
  disabledButton: {
    cursor: 'not-allowed',
    display: 'inline-block',
    lineHeight: 0,
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardTitleIcon: {
    fontSize: '1.25rem',
  },
}));

const IDE_INTEGRATIONS = [
  {
    key: 'cursor',
    annotationKey: 'mcp.avidxchange.com/cursor-install-url',
    label: 'Cursor',
    buttonImage: 'https://cursor.com/deeplink/mcp-install-dark.png',
    buttonAlt: 'Add to Cursor',
  },
] as const;

type IdeKey = typeof IDE_INTEGRATIONS[number]['key'];

type McpServer = {
  displayName: string;
  description: string;
  installUrls: Record<IdeKey, string>;
};

function entityToMcpServer(entity: Entity): McpServer {
  return {
    displayName: entity.metadata.title ?? entity.metadata.name,
    description: entity.metadata.description ?? '',
    installUrls: Object.fromEntries(
      IDE_INTEGRATIONS.map(ide => [
        ide.key,
        entity.metadata.annotations?.[ide.annotationKey] ?? '',
      ])
    ) as Record<IdeKey, string>,
  };
}

function CardTitleWithIcon() {
  const classes = useStyles();
  return (
    <Box className={classes.cardTitle}>
      <CloudIcon className={classes.cardTitleIcon} />
      <span>MCP Servers</span>
    </Box>
  );
}

interface McpServersTableProps {
  variant?: 'flex' | 'fullHeight' | 'gridItem';
}

export const McpServersTable = ({ variant }: McpServersTableProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [servers, setServers] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'Component', 'spec.type': 'mcp-server' } })
      .then(response => {
        setServers(response.items.map(entityToMcpServer));
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [catalogApi]);

  const renderBody = () => {
    if (loading) {
      return (
        <Grid item xs={12} style={{ textAlign: 'center', padding: 24 }}>
          <CircularProgress />
        </Grid>
      );
    }

    if (error) {
      return (
        <Grid item xs={12}>
          <Typography color="error">Failed to load MCP servers: {error.message}</Typography>
        </Grid>
      );
    }

    if (servers.length === 0) {
      return (
        <Grid item xs={12}>
          <Typography color="textSecondary">No MCP servers registered in the catalog yet.</Typography>
        </Grid>
      );
    }

    return (
      <>
        {servers.map(server => (
          <Grid item xs={12} sm={6} md={4} key={server.displayName}>
            <Card className={classes.serverCard}>
              <CardContent className={classes.content}>
                <Box className={classes.serverCardTop}>
                  <Typography className={classes.serverName}>{server.displayName}</Typography>
                  {server.description && (
                    <Typography className={classes.serverDescription}>{server.description}</Typography>
                  )}
                </Box>
                <Box className={classes.serverCardActions}>
                  {IDE_INTEGRATIONS.map(ide =>
                    server.installUrls[ide.key] ? (
                      <React.Fragment key={ide.key}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Available"
                          size="small"
                          className={classes.availableChip}
                        />
                        <a
                          href={server.installUrls[ide.key]}
                          rel="noopener noreferrer"
                          className={classes.installButton}
                        >
                          <img
                            src={ide.buttonImage}
                            alt={ide.buttonAlt}
                            style={{ maxHeight: 32, verticalAlign: 'middle' }}
                          />
                        </a>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={ide.key}>
                        <Chip
                          icon={<WarningIcon />}
                          label="Not yet configured"
                          size="small"
                          className={classes.comingSoonChip}
                        />
                        <Tooltip
                          title={`${ide.label} integration is not yet configured for this server`}
                          placement="top"
                        >
                          <Box className={classes.disabledButton}>
                            <img
                              src={ide.buttonImage}
                              alt={ide.buttonAlt}
                              style={{
                                maxHeight: 32,
                                verticalAlign: 'middle',
                                opacity: 0.4,
                                filter: 'grayscale(100%)',
                                cursor: 'not-allowed',
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </React.Fragment>
                    )
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </>
    );
  };

  return (
    <InfoCard title={<CardTitleWithIcon />} variant={variant}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className={classes.instructionBox}>
            <Typography variant="body2" className={classes.instructionTitle}>
              Getting Started
            </Typography>

            <Typography variant="body2" className={classes.instructionStep}>
              1. Authenticate to Azure Container Registry
            </Typography>
            <Typography variant="body2" component="p" className={classes.instructionIntro}>
              Images are hosted in Azure Container Registry. Sign in and authenticate Docker before pulling:
            </Typography>
            <code className={classes.codeBlock}>az login</code>
            <Typography variant="caption" color="textSecondary" component="p" className={classes.commandHint}>
              Sign in to Azure (once per session or when prompted)
            </Typography>
            <code className={classes.codeBlock}>az acr login --name axglbhubacr</code>
            <Typography variant="caption" color="textSecondary" component="p" className={classes.commandHint}>
              Authenticate Docker to the registry
            </Typography>

            <Box className={classes.stepSeparator}>
              <Typography variant="body2" className={classes.instructionStep}>
                2. Pull the image
              </Typography>
              <code className={classes.codeBlock}>
                docker pull axglbhubacr.azurecr.io/avid-mcp-servers:latest
              </code>
            </Box>

            <Typography variant="body2" className={classes.instructionFooter}>
              For more information, visit{' '}
              <a
                href="https://dev.azure.com/avidxchange/SharedLibraries/_git/ai-toolkit?path=/src/model-context-protocol-servers/README.md&_a=preview"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>MCP Servers Readme</strong>
              </a>.
            </Typography>
          </Box>
        </Grid>
        {renderBody()}
      </Grid>
    </InfoCard>
  );
};
